import { unref, type ComputedRef, type Ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import type { ShipmentStopIdRow } from '@/utils/device-api-shipment-payload';
import type { RoutingStrategy } from '@/components/shipment/form/utils/shipment-form-routing';
import {
  type ShipmentFormStopsPort,
  resolveShipmentFormRoutingBusy,
} from '@/composables/shipment-form-stops-port';
import { persistFormMetadata } from '@/components/shipment/form/utils/persist/persistFormMetadata';
import {
  haveFormStopsChanged,
  putFormStopsOnly,
  stopIdRowsFromFormStore,
} from '@/components/shipment/form/utils/persist/persistFormStops';
import {
  resolveFormStopIdRowsForRoutes,
  syncFormRoutes,
  type RouteSyncMode,
} from '@/components/shipment/form/utils/persist/persistFormRoutes';
import type { RoutePersistIntent } from '@/components/shipment/form/utils/persist/shipment-route-persist-intent';
import {
  persistFormDevices,
  publishFormShipment,
} from '@/components/shipment/form/utils/persist/persistFormDevices';
import {
  createShipmentFormPersistDeps,
  type ShipmentFormPersistDeps,
} from '@/composables/shipment-form-persist-deps';

export function useShipmentFormSave(options: {
  accountId: Ref<number> | ComputedRef<number>;
  shipmentId: Ref<number | null> | ComputedRef<number | null>;
  stopsPort: Ref<ShipmentFormStopsPort | null>;
  routingStrategy?: Ref<RoutingStrategy> | ComputedRef<RoutingStrategy>;
  skipRoutePersistWhenServerRoutesExist?: Ref<boolean> | ComputedRef<boolean>;
  deviceIds?: Ref<readonly number[]> | ComputedRef<readonly number[]>;
}) {
  const queryClient = useQueryClient();
  const persistDeps = createShipmentFormPersistDeps(queryClient);

  function isRoutingBusy(): boolean {
    return resolveShipmentFormRoutingBusy(options.stopsPort.value);
  }

  async function validateStops(): Promise<boolean> {
    const validate = options.stopsPort.value?.validateStops?.();
    if (!validate) return true;
    return validate;
  }

  async function saveMetadata(payload: {
    name: string;
    carrier: string | null;
    notes: string | null;
    asset_value: number | null;
  }) {
    await persistFormMetadata(persistDeps, options, payload);
  }

  function haveStopsChanged(): boolean {
    return haveFormStopsChanged();
  }

  async function putStopsOnly(
    putOptions?: { deferInvalidation?: boolean },
  ): Promise<boolean> {
    return putFormStopsOnly(persistDeps, {
      ...options,
      deferInvalidation: putOptions?.deferInvalidation,
    });
  }

  async function syncRouteSegments(
    mode: RouteSyncMode,
    syncOptions?: {
      deviceIdsOverride?: number[];
      intent?: RoutePersistIntent;
      deferInvalidation?: boolean;
      serverRoutesAlreadyCleared?: boolean;
    },
  ): Promise<boolean> {
    return syncFormRoutes(persistDeps, options, {
      mode,
      ...syncOptions,
    });
  }

  async function assignDevices(deviceIds: readonly number[]) {
    await persistFormDevices(persistDeps, options, deviceIds);
  }

  async function publish() {
    await publishFormShipment(persistDeps, options);
  }

  async function resolveStopIdRowsForRoutes(
    sid: number,
  ): Promise<ShipmentStopIdRow[]> {
    return resolveFormStopIdRowsForRoutes(queryClient, options, sid);
  }

  function stopIdRowsFromStore(): ShipmentStopIdRow[] {
    return stopIdRowsFromFormStore();
  }

  function getPersistDeps(): ShipmentFormPersistDeps {
    return persistDeps;
  }

  return {
    isRoutingBusy,
    validateStops,
    haveStopsChanged,
    saveMetadata,
    putStopsOnly,
    syncRouteSegments,
    assignDevices,
    publish,
    resolveStopIdRowsForRoutes,
    stopIdRowsFromStore,
    getPersistDeps,
  };
}
